/**
 * Einrasten auf der Leinwand: Magnete und Raster.
 *
 * In der Zeitleiste rastet seit jeher alles ein — ein Clip springt an den
 * Schnitt daneben, statt einen Rahmen daneben zu landen. Auf der Leinwand gab
 * es das nicht: eine Bauchbinde mittig zu setzen hieß, sie mit dem Daumen so
 * lange zu schieben, bis es *aussah* wie die Mitte. Sascha, 8. September:
 * „Objektfang … und am Rahmen selbst. Also bei Anfassern, Magnete und
 * Raster.“
 *
 * Sieben Linien je Achse, in dieser Reihenfolge:
 *
 *   - die **Mitte** (0,5) — steht zuerst, damit sie bei gleichem Abstand
 *     gewinnt; sie ist die, die man meistens meint;
 *   - die **Drittel** (1/3, 2/3) — die Linien, an denen man ein Motiv setzt;
 *   - die **Kanten** (0 und 1);
 *   - der **Schutzbereich** (5 % Rand) — so weit innen, dass ein Fernseher
 *     nichts abschneidet.
 *
 * Eingerastet wird nicht nur die Mitte des Objekts, sondern auch seine beiden
 * Kanten. Das ist der Unterschied zwischen „ungefähr mittig“ und „bündig“:
 * wer eine Bauchbinde an den unteren Schutzbereich setzt, will *ihre
 * Unterkante* dort haben und nicht ihren Mittelpunkt.
 *
 * Ohne Browser gerechnet, damit es sich prüfen lässt: hier stehen Zahlen
 * zwischen 0 und 1, keine Bildschirmpunkte.
 */
/** Der Rand, den ein Fernseher sicher zeigt. */
export const SCHUTZ = 0.05;
/** Wie nah es sein muss, in Bildschirmpunkten. Acht ist ein Daumenbreit weniger als spürbar. */
export const FANG_PIXEL = 8;
/**
 * Die Linien, gegen die geprüft wird. Die Reihenfolge entscheidet bei
 * gleichem Abstand — deshalb steht die Mitte vorn.
 */
export const FANGLINIEN = [
    { bei: 0.5, art: 'mitte' },
    { bei: 1 / 3, art: 'drittel' },
    { bei: 2 / 3, art: 'drittel' },
    { bei: 0, art: 'kante' },
    { bei: 1, art: 'kante' },
    { bei: SCHUTZ, art: 'schutz' },
    { bei: 1 - SCHUTZ, art: 'schutz' },
];
/**
 * Eine Achse einrasten.
 *
 * `laenge` ist die Ausdehnung des Objekts auf dieser Achse, ebenfalls als
 * Anteil der Leinwand. Ist sie 0, zählt nur die Mitte — das ist der Fall für
 * etwas, das keine messbare Breite hat.
 */
export function fangeAchse(mitte, laenge, toleranz) {
    let beste = null;
    const versaetze = laenge > 0 ? [0, -laenge / 2, laenge / 2] : [0];
    for (const versatz of versaetze) {
        const punkt = mitte + versatz;
        for (const linie of FANGLINIEN) {
            const abstand = Math.abs(punkt - linie.bei);
            if (abstand > toleranz)
                continue;
            if (!beste || abstand < beste.abstand)
                beste = { abstand, mitte: linie.bei - versatz, linie };
        }
    }
    return beste ? { mitte: beste.mitte, linie: beste.linie } : { mitte, linie: null };
}
/**
 * Beide Achsen auf einmal. Sie hängen nicht zusammen: waagerecht einzurasten
 * darf senkrecht nichts verschieben.
 */
export function fangeKasten(x, y, breite, hoehe, toleranzX, toleranzY) {
    const waagerecht = fangeAchse(x, breite, toleranzX);
    const senkrecht = fangeAchse(y, hoehe, toleranzY);
    return {
        x: waagerecht.mitte,
        y: senkrecht.mitte,
        linien: { x: waagerecht.linie, y: senkrecht.linie },
    };
}
/**
 * Einrasten am Anfasser: nicht die Lage, sondern die *Größe* (7.56b).
 *
 * Beim Ziehen rastet die Ebene seit 7.56 ein, beim Aufziehen bisher nicht —
 * dabei stand in Saschas Satz „bei Anfassern“ zuerst. Eine Ebene bündig an
 * den Bildrand zu bekommen hieß, am Anfasser zu zupfen, bis kein Spalt mehr
 * zu sehen war.
 *
 * Die Mitte bleibt beim Aufziehen stehen, also bewegen sich beide Kanten
 * zugleich. Gesucht wird die Größe, bei der *eine* Kante auf einer der
 * sieben Linien landet: aus `mitte ± breite · groesse / 2 = linie` wird
 * `groesse = 2 · (linie − mitte) / ±breite`. Größen unter null gibt es
 * nicht — eine Linie hinter der Mitte ist keine.
 *
 * `breite`/`hoehe` sind die Ausdehnung der Ebene **bei Größe 1**, als Anteil
 * der Leinwand. Die Toleranzen zählen wie bei `fangeAchse` in Anteilen der
 * Achse, nicht in Größenschritten: acht Punkte bleiben acht Punkte, ob eine
 * Ebene nun die halbe Leinwand füllt oder ein Zehntel.
 */
export function fangeGroesse(groesse, o) {
    const achsen = [
        { mitte: o.x, ausdehnung: o.breite, toleranz: o.toleranzX, achse: 'x' },
        { mitte: o.y, ausdehnung: o.hoehe, toleranz: o.toleranzY, achse: 'y' },
    ];
    let beste = null;
    for (const a of achsen) {
        if (!(a.ausdehnung > 0))
            continue;
        for (const linie of FANGLINIEN) {
            for (const seite of [1, -1]) {
                const kandidat = (2 * (linie.bei - a.mitte) * seite) / a.ausdehnung;
                if (!(kandidat > 0))
                    continue;
                // Der Abstand zählt dort, wo man ihn sieht: an der Kante.
                const abstand = (Math.abs(kandidat - groesse) * a.ausdehnung) / 2;
                if (abstand > a.toleranz)
                    continue;
                if (!beste || abstand < beste.abstand)
                    beste = { abstand, groesse: kandidat };
            }
        }
    }
    const gewaehlt = beste ? beste.groesse : groesse;
    // Welche Linien bei *dieser* Größe getroffen sind — auch die der anderen
    // Achse, wenn sie zufällig mitkommt (ein Quadrat in der Mitte trifft beide).
    const linien = { x: null, y: null };
    if (beste) {
        for (const a of achsen) {
            if (!(a.ausdehnung > 0))
                continue;
            for (const linie of FANGLINIEN) {
                for (const seite of [1, -1]) {
                    const kante = a.mitte + (seite * a.ausdehnung * gewaehlt) / 2;
                    if (Math.abs(kante - linie.bei) < 1e-9 && !linien[a.achse])
                        linien[a.achse] = linie;
                }
            }
        }
    }
    return { groesse: gewaehlt, linien };
}
export const RASTER_WAHLEN = [
    { id: 'aus', label: 'Aus' },
    { id: 'drittel', label: 'Drittel' },
    { id: 'schutz', label: 'Schutzbereich' },
    { id: 'beides', label: 'Beides' },
];
/**
 * Welche Linien bei dieser Wahl dauerhaft liegen.
 *
 * Die Mitte und die Kanten sind nie dabei: die Kante ist der Bildrand, den
 * sieht man, und die Mitte hilft beim Ausrichten weniger als die Drittel --
 * beim Ziehen rastet man trotzdem weiter an ihr ein.
 */
export function rasterLinien(wahl) {
    const arten = wahl === 'drittel' ? ['drittel'] : wahl === 'schutz' ? ['schutz'] : wahl === 'beides' ? ['drittel', 'schutz'] : [];
    return FANGLINIEN.filter((l) => arten.includes(l.art));
}
//# sourceMappingURL=fang.js.map